import { memo, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarCheck,
  FileClock,
  LogOut,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type {
  UpcomingChange,
  UpcomingChangeType,
} from '@gokrew/models/company-dashboard';
import { formatDate } from '@gokrew/ui/utility/utility';
import { cn } from '@gokrew/ui/lib/utils';
import { TranslationNS } from '@/constants/translation-namespaces';
import { useUpcomingChanges } from '@/hooks/dashboard/useDashboard';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import styles from './UpcomingChangesDrawer.module.css';

const TYPE_ICON: Record<UpcomingChangeType, LucideIcon> = {
  start: UserPlus,
  departure: LogOut,
  probation_end: CalendarCheck,
  contract_end: FileClock,
};

interface UpcomingChangesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * "Who and when" behind the Upcoming changes card (spec 1.1, D62): every
 * start, scheduled departure, probation end and contract end in the next 30
 * days, grouped by date. Each person opens their profile.
 */
const UpcomingChangesDrawer = ({
  open,
  onOpenChange,
}: UpcomingChangesDrawerProps) => {
  const { t, i18n } = useTranslation(TranslationNS.Dashboard);
  const navigate = useNavigate();
  const { data, isLoading, isError } = useUpcomingChanges(open);
  const locale = i18n.language;

  const byDate = useMemo(() => {
    const groups = new Map<string, UpcomingChange[]>();
    for (const change of data?.changes ?? []) {
      groups.set(change.date, [...(groups.get(change.date) ?? []), change]);
    }
    return [...groups.entries()];
  }, [data]);

  const openPerson = useCallback(
    (employeeId: string) => {
      onOpenChange(false);
      navigate(`/employees/detail/${employeeId}/info`);
    },
    [navigate, onOpenChange]
  );

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction="right">
      <DrawerContent className={styles.drawer}>
        <DrawerHeader>
          <DrawerTitle>{t('upcoming.title')}</DrawerTitle>
          <DrawerDescription>
            {t('upcoming.description', {
              count: data?.changes.length ?? 0,
            })}
          </DrawerDescription>
        </DrawerHeader>
        <div className={styles.body}>
          {isLoading && <div className={styles.skeleton} aria-busy="true" />}
          {isError && (
            <p className={styles.message} role="alert">
              {t('upcoming.error')}
            </p>
          )}
          {data && !data.changes.length && (
            <p className={styles.message}>{t('summary.nothing_upcoming')}</p>
          )}
          {byDate.map(([date, changes]) => (
            <section key={date} className={styles.day}>
              <h3 className={styles.date}>
                {formatDate(date, { format: 'dddd D MMMM', locale })}
              </h3>
              <ul className={styles.list}>
                {changes.map((change) => (
                  <ChangeRow
                    key={`${change.type}:${change.employeeId}`}
                    change={change}
                    onOpen={openPerson}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const ChangeRow = memo(
  ({
    change,
    onOpen,
  }: {
    change: UpcomingChange;
    onOpen: (employeeId: string) => void;
  }) => {
    const { t } = useTranslation(TranslationNS.Dashboard);
    const Icon = TYPE_ICON[change.type];
    return (
      <li>
        <button
          type="button"
          className={cn(styles.row, styles[change.type])}
          onClick={() => onOpen(change.employeeId)}
        >
          <Icon className={styles.icon} aria-hidden="true" />
          <span className={styles.name}>{change.name}</span>
          <span className={styles.type}>
            {t(`upcoming.type.${change.type}`)}
          </span>
        </button>
      </li>
    );
  }
);
ChangeRow.displayName = 'ChangeRow';

export default UpcomingChangesDrawer;
